/*
 * A boxed sentence with a glyph, in one of three kinds. `danger` is the refusal of a form or a
 * failed load (`role="alert"`: it interrupts, because a person who is typing does not look elsewhere
 * on the page), `warning` a notice the reader should weigh before going on, `success` the word that
 * something worked (`role="status"`: it is read when the reader is free). The glyph repeats the
 * kind for the eye and is hidden from assistive technology, which hears the role instead.
 *
 * `Alert` is the box. `FormAlert` is the form's use of it: it takes the locale **key** of the
 * refusal, which is what `useAsyncAction` keeps and what the server sends (protocol.md §1.1) —
 * `translate` renders a key this build does not know as a sentence rather than as the raw key — and
 * moves the focus to the sentence, because a screen reader that is reading a field does not hear a
 * change beside it unless the focus goes there. A caller that has already composed its own sentence
 * passes `message` instead.
 */

import { useEffect, useRef, type ReactNode } from "react"
import { translate } from "../core/i18n/index.ts"
import { Icon, type IconName } from "../core/shell/icons.tsx"
import { cx } from "../lib/cx.ts"
import "./ui.css"

export type AlertKind = "danger" | "warning" | "success"

const GLYPH: Record<AlertKind, IconName> = {
	danger: "triangleAlert",
	warning: "circleAlert",
	success: "circleCheck",
}

const ROLE: Record<AlertKind, "alert" | "status"> = {
	danger: "alert",
	warning: "status",
	success: "status",
}

export interface AlertProps {
	kind?: AlertKind
	children: ReactNode
	/** A control at the end of the line — *Try again* under a failed load. */
	action?: ReactNode
	className?: string
	/** Present when the sentence is something the focus should land on. */
	ref?: React.Ref<HTMLDivElement>
	tabIndex?: number
}

export function Alert({ kind = "danger", children, action, className, ref, tabIndex }: AlertProps) {
	return (
		<div
			className={cx("tb-alert", `tb-alert--${kind}`, className)}
			role={ROLE[kind]}
			ref={ref}
			tabIndex={tabIndex}
		>
			<Icon name={GLYPH[kind]} />
			<p className="tb-alert__text">{children}</p>
			{action === undefined ? null : <span className="tb-alert__action">{action}</span>}
		</div>
	)
}

interface FormAlertProps {
	kind?: AlertKind
	/** The locale key of the refusal; null or "" renders nothing. */
	messageKey?: string | null
	/** Already translated text, for a caller that composed the sentence itself. */
	message?: string
}

export function FormAlert({ kind = "danger", messageKey, message }: FormAlertProps) {
	const ref = useRef<HTMLDivElement>(null)
	const text = messageKey ? translate(messageKey) : (message ?? "")

	// Only a refusal takes the focus: a success or a warning is announced by its role and must not
	// pull a person out of the field they are in.
	useEffect(() => {
		if (text !== "" && kind === "danger") ref.current?.focus()
	}, [text, kind])

	if (text === "") return null
	return (
		<Alert kind={kind} ref={ref} tabIndex={-1}>
			{text}
		</Alert>
	)
}
